import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from './config';
import PhoneNumberInput from './PhoneNumberInput';

const emptyDraft = { email: '', name: '', role: 'manager', stores: [], active: true };

const UserAdmin = ({ stores, currentUser, onStoresChange }) => {
    const [users, setUsers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [draft, setDraft] = useState(emptyDraft);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('users');
    const [storePhone, setStorePhone] = useState('');
    const [storeName, setStoreName] = useState('');
    const [storeError, setStoreError] = useState(null);
    const [addingStore, setAddingStore] = useState(false);
    const [editingStore, setEditingStore] = useState(null);
    const [storeDraft, setStoreDraft] = useState({ name: '', phone: '' });

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/users`);
            setUsers(response.data);
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Could not load users.');
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

        const startEditStore = (store) => {
        setEditingStore(store._id);
        setStoreDraft({ name: store.name, phone: store.phone || '' });
        setStoreError(null);
    };

    const cancelEditStore = () => {
        setEditingStore(null);
        setStoreDraft({ name: '', phone: '' });
    };

    const phoneError = (value) => {
        const digits = (value || '').replace(/\D/g, '');
        if (digits.length === 0) return 'Store phone is required';
        if (digits.length !== 10) return 'Phone must be 10 digits';
        return null;
    };

    const saveStore = async () => {
        if (!storeDraft.name.trim()) {
            setStoreError('Store name is required');
            return;
        }

        const phoneProblem = phoneError(storeDraft.phone);
        if (phoneProblem) {
            setStoreError(phoneProblem);
            return;
        }

        setAddingStore(true);
        setStoreError(null);

        try {
            await axios.put(`${API_BASE}/api/stores/${editingStore}`, {
                name: storeDraft.name.trim(),
                phone: storeDraft.phone.trim()
            });
            if (onStoresChange) await onStoresChange();
            cancelEditStore();
        } catch (err) {
            console.error('Error updating store:', err);
            setStoreError(err.response?.data?.message || 'Could not update that store.');
        } finally {
            setAddingStore(false);
        }
    };

    const handleAddStore = async () => {
        if (!storeName.trim()) {
            setStoreError('Store name is required');
            return;
        }

        const phoneProblem = phoneError(storePhone);
        if (phoneProblem) {
            setStoreError(phoneProblem);
            return;
        }

        setAddingStore(true);
        setStoreError(null);

        try {
            await axios.post(`${API_BASE}/api/stores`, {
                name: storeName.trim(),
                phone: storePhone.trim()
            });
            setStoreName('');
            setStorePhone('');
            if (onStoresChange) await onStoresChange();
        } catch (err) {
            console.error('Error creating store:', err);
            setStoreError(err.response?.data?.message || 'Could not create that store.');
        } finally {
            setAddingStore(false);
        }
    };



    const handleAdd = () => {
        setEditingUser(null);
        setDraft(emptyDraft);
        setError(null);
        setModalOpen(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setDraft({
            email: user.email,
            name: user.name || '',
            role: user.role,
            stores: (user.stores || []).map(s => s._id || s),
            active: user.active
        });
        setError(null);
        setModalOpen(true);
    };

    const toggleStore = (storeId) => {
        setDraft(prev => ({
            ...prev,
            stores: prev.stores.includes(storeId)
                ? prev.stores.filter(id => id !== storeId)
                : [...prev.stores, storeId]
        }));
    };

    const handleSave = async () => {
        if (!editingUser && !draft.email.trim()) {
            setError('Email is required');
            return;
        }
        if (draft.stores.length === 0) {
            setError('Select at least one store');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (editingUser) {
                await axios.put(`${API_BASE}/api/users/${editingUser._id}`, {
                    name: draft.name,
                    role: draft.role,
                    stores: draft.stores,
                    active: draft.active
                });
            } else {
                await axios.post(`${API_BASE}/api/users`, draft);
            }
            await fetchUsers();
            setModalOpen(false);
        } catch (err) {
            console.error('Error saving user:', err);
            setError(err.response?.data?.message || 'Could not save that user.');
        } finally {
            setSaving(false);
        }
    };

    const isSelf = editingUser && String(editingUser._id) === String(currentUser._id);

    return (
        <div className="user-admin">
            <div className="admin-tabs">
                <div
                    className={activeTab === 'users' ? 'admin-tab active' : 'admin-tab'}
                    onClick={() => setActiveTab('users')}
                >
                    Users
                </div>
                <div
                    className={activeTab === 'stores' ? 'admin-tab active' : 'admin-tab'}
                    onClick={() => setActiveTab('stores')}
                >
                    Stores
                </div>
            </div>

                        {activeTab === 'stores' && (
                <>
                    <div className="tab">
                        <h2>Stores</h2>

                        <div className="stores-list">
                            {stores.map(store => (
                                <div
                                    key={store._id}
                                    className={editingStore === store._id ? 'stores-item editing' : 'stores-item'}
                                >
                                    {editingStore === store._id ? (
                                        <>
                                            <input
                                                type="text"
                                                value={storeDraft.name}
                                                onChange={(e) => setStoreDraft({ ...storeDraft, name: e.target.value })}
                                                placeholder="Store name"
                                            />
                                            <PhoneNumberInput
                                                value={storeDraft.phone}
                                                onChange={(val) => setStoreDraft({ ...storeDraft, phone: val })}
                                                placeholder="___-___-____"
                                            />
                                            <div className="btnwrap">
                                                <button className="savebtn" onClick={saveStore} disabled={addingStore}>
                                                    {addingStore ? '…' : 'Save'}
                                                </button>
                                                <button className="cxlbtn" onClick={cancelEditStore}>Cancel</button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                        <div>
                                            <div>{store.name}</div>
                                            <div>{store.phone}</div>
                                        </div>
                                            <button className="editbtn" onClick={() => startEditStore(store)}>Edit</button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="store-add">
                            <h2>Add Store</h2>
                            <input
                                type="text"
                                value={storeName}
                                onChange={(e) => setStoreName(e.target.value)}
                                placeholder="Store name"
                            />
                            <PhoneNumberInput
                                value={storePhone}
                                onChange={setStorePhone}
                                placeholder="___-___-____"
                            />
                            <button className="savebtn" onClick={handleAddStore} disabled={addingStore}>
                                {addingStore ? 'Adding…' : 'Add Store'}
                            </button>
                        </div>

                        {storeError && <div className="error-message">{storeError}</div>}
                    </div>
                </>
            )}


            {activeTab === 'users' && (
                <>
                <div className="tab">
                    <h2>Users</h2>

                    <button className="btn-lg" onClick={handleAdd}>Add User</button>


                    {error && !modalOpen && <div className="error-message">{error}</div>}
                    <div className="user-heading">
                        <div>Name</div>
                        <div>Email</div>
                        <div>Role</div>
                        <div>Store(s)</div>
                        <div>Last Login</div>
                        <div>Activity</div>
                    </div>
                    <div className="user-list">
                        {users.map(user => (
                            <div
                                key={user._id}
                                className={user.active ? 'user-item' : 'user-item inactive'}
                            >
                                <div>{user.name || '—'}</div>
                                <div>{user.email}</div>
                                <div>{user.role}</div>
                                <div>{(user.stores || []).map(s => s.name).join(', ')}</div>
                                <div>
                                    {user.lastLogin
                                        ? new Date(user.lastLogin).toLocaleDateString()
                                        : 'Never signed in'}
                                </div>
                                <div>{user.active ? 'Active' : 'Inactive'}</div>
                                <div>
                                    <button className="editbtn" onClick={() => handleEdit(user)}>Edit</button>
                                </div>
                                
                            </div>
                        ))}
                    </div>
                    </div>
                </>
            )}
            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>

                        <div className="form-section-basic">
                            <label>Email
                                <input
                                    type="email"
                                    value={draft.email}
                                    disabled={!!editingUser}
                                    onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                                    placeholder="name@example.com"
                                />
                            </label>

                            <label>Name
                                <input
                                    type="text"
                                    value={draft.name}
                                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                    placeholder="Optional — filled in on first sign-in"
                                />
                            </label>

                            <label>Role
                                <select
                                    value={draft.role}
                                    disabled={isSelf}
                                    onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                                >
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </label>

                            <fieldset className="store-checkboxes">
                                <legend>Stores</legend>
                                {stores.map(store => (
                                    <label key={store._id} className="checkbox-label admin-user-cb">
                                        <input
                                            type="checkbox"
                                            checked={draft.stores.includes(store._id)}
                                            onChange={() => toggleStore(store._id)}
                                        />
                                        {store.name}
                                    </label>
                                ))}
                            </fieldset>

                            {editingUser && (
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={draft.active}
                                        disabled={isSelf}
                                        onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                                    />
                                    Active
                                </label>
                            )}
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="form-section-actions">
                            <button className="updatebtn" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : (editingUser ? 'Update' : 'Add User')}
                            </button>
                            <button type="button" onClick={() => setModalOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAdmin;